import type { ReactNode } from "react";
import { SectionHeader } from "@shared-kernel/primary/shared/section-header/section-header.content";
import { EmptyState } from "@shared-kernel/primary/shared/layout/state-components/empty-state";
import type { EmptyStateProps } from "@shared-kernel/primary/shared/layout/types";
import { CardList } from "./card-list.content";

interface CardListSectionProps<T> {
  title?: string;
  items: T[];
  /** Returns the key used for grouping items (displayed as group label) */
  getGroupingKeyAndLabel: (item: T) => string;
  /** Returns a sortable date value (ISO string or Date) for sorting groups and items */
  getSortDate: (item: T) => string | Date;
  getKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  /** Props for empty state when items array is empty */
  emptyStateProps: EmptyStateProps;
  /** Sort order for items and groups. Defaults to "desc" (newest first) */
  sortOrder?: "asc" | "desc";
}

export function CardListSection<T>({
  title,
  items,
  getGroupingKeyAndLabel,
  getKey,
  renderItem,
  getSortDate,
  emptyStateProps,
  sortOrder,
}: CardListSectionProps<T>) {
  const isEmpty = items.length === 0;

  return (
    <div className="w-full">
      {title && <SectionHeader title={title} />}
      {isEmpty ? (
        <EmptyState {...emptyStateProps} />
      ) : (
        <CardList
          items={items}
          getGroupingKeyAndLabel={getGroupingKeyAndLabel}
          getKey={getKey}
          renderItem={renderItem}
          getSortDate={getSortDate}
          sortOrder={sortOrder}
        />
      )}
    </div>
  );
}
